import BaseService from './BaseService'

class InventoryLogService extends BaseService {
  constructor() {
    super('/inventory-logs')
  }

  /**
   * Get inventory logs with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.product_id - Filter by product ID
   * @param {number} params.user_id - Filter by user ID
   * @param {string} params.operation - Filter by operation type
   * @param {string} params.start_date - Filter by start date (YYYY-MM-DD)
   * @param {string} params.end_date - Filter by end date (YYYY-MM-DD)
   * @param {string} params.search - Search term
   * @param {number} params.per_page - Items per page (1-100)
   * @param {string} params.sort_by - Sort field
   * @param {string} params.sort_direction - Sort direction (asc/desc)
   * @returns {Promise<Object>} Inventory logs with pagination
   */
  async getInventoryLogs(params = {}) {
    return this.getAll(params)
  }

  /**
   * Get inventory log summary statistics
   * @param {Object} params - Query parameters
   * @param {string} params.start_date - Filter by start date (YYYY-MM-DD)
   * @param {string} params.end_date - Filter by end date (YYYY-MM-DD)
   * @returns {Promise<Object>} Summary statistics
   */
  async getInventoryLogSummary(params = {}) {
    return this.customRequest('get', '/summary', null, { params })
  }

  /**
   * Get a specific inventory log by ID
   * @param {number} logId - Inventory log ID
   * @returns {Promise<Object>} Inventory log details
   */
  async getInventoryLog(logId) {
    return this.getById(logId)
  }

  /**
   * Export inventory logs as CSV
   * @param {Object} params - Query parameters (same as getInventoryLogs)
   * @returns {Promise<Blob>} CSV file blob
   */
  async exportInventoryLogs(params = {}) {
    return this.customRequest('get', '/export', null, {
      params: { ...params, format: 'csv' },
      responseType: 'blob'
    })
  }

  /**
   * Get available operation types for filtering
   * @returns {Array} Array of operation types
   */
  getOperationTypes() {
    return [
      { value: null, label: 'All Operations' },
      { value: 'sale', label: 'Sale' },
      { value: 'void', label: 'Sale Void' },
      { value: 'adjustment', label: 'Manual Adjustment' },
      { value: 'restock', label: 'Restock' },
      { value: 'initial', label: 'Initial Stock' }
    ]
  }

  /**
   * Get sort options for inventory logs
   * @returns {Array} Array of sort options
   */
  getSortOptions() {
    return [
      { value: 'created_at', label: 'Date' },
      { value: 'product_id', label: 'Product' },
      { value: 'operation', label: 'Operation' },
      { value: 'quantity_change', label: 'Quantity Change' }
    ]
  }
}

export const inventoryLogService = new InventoryLogService()
